import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import EntityCombobox from "@/components/EntityCombobox";
import { useAuth } from "@/auth";
import {
  CURRENCY,
  todayIso,
  round2,
  type GlAccount,
  type GlJournalDetail,
} from "@/lib/gl";

interface LineRow {
  account_id: number | null;
  debit: string;
  credit: string;
  description: string;
}

const EMPTY_LINE: LineRow = {
  account_id: null,
  debit: "",
  credit: "",
  description: "",
};

function fmt(n: number) {
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Display label for an account in the searchable picker. Keeping the code and
// name together (with the type on the secondary line) lets the combobox filter
// by code, name, and type in one pass.
function accountLabel(a: GlAccount) {
  return `${a.code} — ${a.name}`;
}

export default function GeneralLedgerJournalForm() {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canPost = can("gl.post");

  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [entryDate, setEntryDate] = useState(
    searchParams.get("date") || todayIso(),
  );
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<LineRow[]>([
    { ...EMPTY_LINE },
    { ...EMPTY_LINE },
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [accRes, journal] = await Promise.all([
          api.get<{ accounts: GlAccount[] }>(
            "/gl/accounts?is_active=1",
          ),
          editing
            ? api.get<{ journal: GlJournalDetail }>(`/gl/journals/${id}`)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setAccounts(accRes.accounts);
        if (journal) {
          if (journal.journal.status !== "draft") {
            toast.error("Only draft journals can be edited");
            navigate(`/general-ledger/journals/${id}`, { replace: true });
            return;
          }
          setEntryDate(journal.journal.entry_date);
          setDescription(journal.journal.description ?? "");
          setLines(
            journal.journal.lines.map((l) => ({
              account_id: l.account_id,
              debit: l.debit > 0 ? String(l.debit) : "",
              credit: l.credit > 0 ? String(l.credit) : "",
              description: l.description ?? "",
            })),
          );
        }
      } catch (err) {
        if (!cancelled) toast.error((err as Error).message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const accountById = useMemo(() => {
    const m = new Map<number, GlAccount>();
    for (const a of accounts) m.set(a.id, a);
    return m;
  }, [accounts]);

  const totals = useMemo(() => {
    let d = 0;
    let c = 0;
    for (const l of lines) {
      const dv = Number(l.debit || 0);
      const cv = Number(l.credit || 0);
      if (Number.isFinite(dv)) d += dv;
      if (Number.isFinite(cv)) c += cv;
    }
    return {
      debit: round2(d),
      credit: round2(c),
      diff: round2(d - c),
      balanced: Math.abs(d - c) < 0.005 && d > 0,
    };
  }, [lines]);

  const updateLine = (i: number, patch: Partial<LineRow>) => {
    setLines((arr) => {
      const next = [...arr];
      next[i] = { ...next[i]!, ...patch };
      return next;
    });
  };

  const addLine = () => setLines((arr) => [...arr, { ...EMPTY_LINE }]);
  const removeLine = (i: number) =>
    setLines((arr) =>
      arr.length <= 2 ? arr : arr.filter((_, idx) => idx !== i),
    );

  const buildPayload = () => {
    const cleaned = lines
      .map((l) => ({
        account_id: l.account_id ?? 0,
        debit: Number(l.debit || 0),
        credit: Number(l.credit || 0),
        description: l.description.trim() || null,
      }))
      .filter((l) => l.debit > 0 || l.credit > 0);
    return {
      entry_date: entryDate,
      description: description.trim() || null,
      lines: cleaned,
    };
  };

  const validate = () => {
    if (!entryDate) return "Entry date is required";
    if (lines.length < 2) return "At least two lines are required";
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!;
      const d = Number(l.debit || 0);
      const c = Number(l.credit || 0);
      if (d > 0 && c > 0)
        return `Line ${i + 1}: cannot be both debit and credit`;
      if ((d > 0 || c > 0) && !l.account_id)
        return `Line ${i + 1}: account is required`;
    }
    if (!totals.balanced) return "Journal is unbalanced";
    return null;
  };

  const save = async (post: boolean) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/gl/journals/${id}`, buildPayload());
        if (post) await api.post(`/gl/journals/${id}/post`);
        toast.success(post ? "Journal posted" : "Draft saved");
        navigate(`/general-ledger/journals/${id}`);
      } else {
        const res = await api.post<{ id: number; reference: string; status: string }>(
          "/gl/journals",
          { ...buildPayload(), post },
        );
        toast.success(post ? `Posted ${res.reference}` : `Draft ${res.reference} saved`);
        navigate(`/general-ledger/journals/${res.id}`);
      }
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as { error?: string } | null)?.error ?? e.message)
          : (e as Error).message;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/general-ledger/journals">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to journals
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={saving || loading}
            onClick={() => save(false)}
          >
            <Save className="h-4 w-4 mr-1" />
            Save Draft
          </Button>
          <Button
            disabled={saving || loading || !canPost || !totals.balanced}
            onClick={() => save(true)}
            title={!canPost ? "You don't have gl.post" : undefined}
          >
            <Send className="h-4 w-4 mr-1" />
            {editing ? "Save & Post" : "Post Journal"}
          </Button>
        </div>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-lg">
            {editing ? "Edit Draft Journal" : "New Journal Entry"}
          </CardTitle>
          <CardDescription>
            Every entry must balance — total debits must equal total credits.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="entry_date">Entry Date</Label>
              <Input
                id="entry_date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this journal record?"
              />
            </div>
          </div>

          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Account</TableHead>
                  <TableHead>Memo</TableHead>
                  <TableHead className="text-right w-[140px]">Debit</TableHead>
                  <TableHead className="text-right w-[140px]">Credit</TableHead>
                  <TableHead className="w-[40px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <EntityCombobox
                        aria-label={`Line ${i + 1} account`}
                        placeholder="Select account"
                        value={
                          l.account_id
                            ? (() => {
                                const a = accountById.get(l.account_id);
                                return a ? accountLabel(a) : "";
                              })()
                            : ""
                        }
                        options={accounts.map((a) => ({
                          id: a.id,
                          name: accountLabel(a),
                          secondary: a.type,
                        }))}
                        emptyText="No accounts available."
                        onSelect={(opt) =>
                          updateLine(i, { account_id: Number(opt.id) })
                        }
                        onClear={() => updateLine(i, { account_id: null })}
                      />
                      {l.account_id && (
                        <div className="text-xs text-muted-foreground mt-1 capitalize">
                          {accountById.get(l.account_id)?.type ?? ""}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        value={l.description}
                        onChange={(e) =>
                          updateLine(i, { description: e.target.value })
                        }
                        placeholder="Line note (optional)"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={l.debit}
                        onChange={(e) =>
                          updateLine(i, {
                            debit: e.target.value,
                            credit: e.target.value ? "" : l.credit,
                          })
                        }
                        className="text-right font-mono"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={l.credit}
                        onChange={(e) =>
                          updateLine(i, {
                            credit: e.target.value,
                            debit: e.target.value ? "" : l.debit,
                          })
                        }
                        className="text-right font-mono"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(i)}
                        disabled={lines.length <= 2}
                        title="Remove line"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" onClick={addLine}>
              <Plus className="h-4 w-4 mr-1" />
              Add Line
            </Button>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Debits: </span>
                <span className="font-mono font-semibold">
                  {fmt(totals.debit)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Credits: </span>
                <span className="font-mono font-semibold">
                  {fmt(totals.credit)}
                </span>
              </div>
              {totals.balanced ? (
                <Badge
                  variant="outline"
                  className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                >
                  Balanced
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
                >
                  Out of balance: {fmt(Math.abs(totals.diff))}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
